import React, { useState, useEffect } from 'react';
import { useNotification } from './Notification';
import { CrownIcon } from './Icons';

const VIP_LEVELS = {
  1: { title: "VIP Argent", icon: 'https://i.postimg.cc/gjdFVPLC/VIP_1.png' },
  2: { title: "VIP Or", icon: 'https://i.postimg.cc/7hD83yTv/VIP_2.png' },
  3: { title: "VIP Platine", icon: 'https://i.postimg.cc/qq0VsdCV/VIP_3.png' },
  4: { title: "VIP Adjudicateur", icon: 'https://i.postimg.cc/gjdFVP6P/VIP_4.png' },
  5: { title: "VIP Grande Table", icon: 'https://i.postimg.cc/fyZnchS1/VIP_5.png' }
};

function VIPManager() {
  const [activeVIPs, setActiveVIPs] = useState([]);
  const [expiredVIPs, setExpiredVIPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExpired, setShowExpired] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { showNotification } = useNotification();

  useEffect(() => {
    fetchVIPs();
  }, []);

  const fetchVIPs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/vips', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setActiveVIPs(data.active || []);
        setExpiredVIPs(data.expired || []);
      } else {
        showNotification('Erreur lors de la récupération des VIP', 'error');
      }
    } catch (error) {
      console.error('Erreur récupération VIP:', error);
      showNotification('Erreur de connexion', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(parseInt(timestamp));
    return date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRemainingTime = (expirationTimestamp) => {
    if (!expirationTimestamp) return 'N/A';
    const now = Date.now();
    const expiration = parseInt(expirationTimestamp);
    const diff = expiration - now;

    if (diff <= 0) return 'Expiré';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) {
      return `${days}j ${hours}h`;
    }
    return `${hours}h`;
  };

  const filterVIPs = (vips) => {
    if (!searchTerm) return vips;
    
    const term = searchTerm.toLowerCase();
    return vips.filter(vip => 
      (vip.player_name && vip.player_name.toLowerCase().includes(term)) ||
      (vip.discord_id && vip.discord_id.includes(term)) ||
      (vip.identifier && vip.identifier.includes(term))
    );
  };

  const getVIPInfo = (level) => {
    return VIP_LEVELS[level] || { title: 'VIP Inconnu', icon: '' };
  };

  const VIPCard = ({ vip, isExpired }) => {
    const vipInfo = getVIPInfo(vip.vip_level);
    const remainingTime = getRemainingTime(vip.vip_expiration);
    const isExpiringSoon = !isExpired && parseInt(vip.vip_expiration) - Date.now() < 7 * 24 * 60 * 60 * 1000; // moins de 7 jours

    return (
      <div className={`vip-card ${isExpired ? 'expired' : ''} ${isExpiringSoon ? 'expiring-soon' : ''}`}>
        <div className="vip-card-header">
          <div className="vip-icon-container">
            {vipInfo.icon ? (
              <img src={vipInfo.icon} alt={vipInfo.title} className="vip-icon" />
            ) : (
              <div className="vip-icon-placeholder">VIP</div>
            )}
          </div>
          <div className="vip-title">
            <h3>{vipInfo.title}</h3>
            <span className="vip-level">Niveau {vip.vip_level}</span>
          </div>
        </div>
        
        <div className="vip-card-body">
          <div className="vip-info-row">
            <span className="vip-label">Joueur:</span>
            <span className="vip-value">{vip.player_name || 'Inconnu'}</span>
          </div>
          
          <div className="vip-info-row">
            <span className="vip-label">ID Discord:</span>
            <span className="vip-value">{vip.discord_id || 'Non renseigné'}</span>
          </div>
          
          <div className="vip-info-row">
            <span className="vip-label">Identifier:</span>
            <span className="vip-value identifier">{vip.identifier}</span>
          </div>
          
          {vip.rp_firstname && vip.rp_lastname && (
            <div className="vip-info-row">
              <span className="vip-label">Nom RP:</span>
              <span className="vip-value">{vip.rp_firstname} {vip.rp_lastname}</span>
            </div>
          )}
          
          <div className="vip-info-row">
            <span className="vip-label">Activation:</span>
            <span className="vip-value">{formatDate(vip.activation_date)}</span>
          </div>
          
          <div className="vip-info-row">
            <span className="vip-label">Expiration:</span>
            <span className="vip-value">{formatDate(vip.vip_expiration)}</span>
          </div>
          
          {!isExpired && (
            <div className="vip-info-row">
              <span className="vip-label">Temps restant:</span>
              <span className={`vip-value ${isExpiringSoon ? 'warning' : 'success'}`}>
                {remainingTime}
              </span>
            </div>
          )}
          
          {vip.code && (
            <div className="vip-info-row">
              <span className="vip-label">Code Tebex:</span>
              <span className="vip-value code">{vip.code}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const filteredActiveVIPs = filterVIPs(activeVIPs);
  const filteredExpiredVIPs = filterVIPs(expiredVIPs);

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Chargement des VIP...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <CrownIcon size={28} />
          Gestion des VIP
        </h1>
        <p>Suivi des VIP actifs et expirés du serveur</p>
      </div>

      <div className="vip-stats">
        <div className="stat-card">
          <div className="stat-value">{activeVIPs.length}</div>
          <div className="stat-label">VIP Actifs</div>
        </div>
        <div className="stat-card expired">
          <div className="stat-value">{expiredVIPs.length}</div>
          <div className="stat-label">VIP Expirés</div>
        </div>
      </div>

      <div className="vip-controls">
        <input
          type="text"
          placeholder="Rechercher par nom, Discord ID ou identifier..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        
        <button
          className={`btn ${showExpired ? 'btn-secondary' : 'btn-primary'}`}
          onClick={() => setShowExpired(!showExpired)}
        >
          {showExpired ? 'Voir les VIP actifs' : 'Voir les VIP expirés'}
        </button>
      </div>

      {!showExpired ? (
        <div className="vip-section">
          <h2>VIP Actifs ({filteredActiveVIPs.length})</h2>
          {filteredActiveVIPs.length === 0 ? (
            <div className="empty-state">
              <p>Aucun VIP actif{searchTerm ? ' ne correspond à votre recherche' : ''}</p>
            </div>
          ) : (
            <div className="vip-grid">
              {filteredActiveVIPs.map((vip, index) => (
                <VIPCard key={index} vip={vip} isExpired={false} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="vip-section">
          <h2>VIP Expirés ({filteredExpiredVIPs.length})</h2>
          {filteredExpiredVIPs.length === 0 ? (
            <div className="empty-state">
              <p>Aucun VIP expiré{searchTerm ? ' ne correspond à votre recherche' : ''}</p>
            </div>
          ) : (
            <div className="vip-grid">
              {filteredExpiredVIPs.map((vip, index) => (
                <VIPCard key={index} vip={vip} isExpired={true} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default VIPManager;
